import { DT } from "../../constants.ts";
import { add, scale } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, MoveKind, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyStatus, coneConstructs, coneTargets, damageBarrier, explode, isEnemy, playersInRadius, spawnProjectile } from "../../sim/combat.ts";
import { damageConstruct } from "../../sim/constructs.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { fxk, pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, contactEnemies, knockUp, ownerAlive, ready, secs, simpleProjectile, spreadDir, startMove, trigger, useUlt, wishDir } from "../kitlib.ts";

export const V = { combo: 0, charge: 1, ultGlow: 2, slashReady: 3, touched: 4 } as const;
export const PK = { RiftSlash: pk(HeroId.Tragik, 0) } as const;
export const FX = { Rift: fxk(HeroId.Tragik, 0) } as const;

const HERO_DASH = MoveKind.Hero + 0;
const ULT_TICKS = 12 * 60;
const riftSlashProjectile = simpleProjectile(
  (sim, p, owner, hit) => {
    if (!ownerAlive(sim, owner)) return;
    const dmg = hv(sim.c, owner, V.slashReady) || 45;
    if (hit.target !== undefined) {
      applyDamage(sim, hit.target, dmg, owner, { pos: hit.pos });
      const idx = owner * 6 + Slot.Ability1;
      sim.c.Abil.cd[idx] = Math.max(0, sim.c.Abil.cd[idx]! - secs(1));
      return;
    }
    if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, dmg, owner);
    else sim.emit({ type: "impact", pos: hit.pos, kind: FX.Rift });
    void p;
  },
  { color: "#ff7a3d", size: 0.22, trail: true },
  { mode: "enemies" },
);
riftSlashProjectile.pierce = 2;

const def: HeroDef = {
  id: HeroId.Tragik,
  slug: "tragik",
  name: "Tragik",
  role: Role.Damage,
  color: "#f06a3b",
  blurb: "A portal-sword duelist who chains limbo dashes into cleaves and a towering dark form.",
  health: 150,
  armor: 0,
  shields: 125,
  radius: 0.42,
  height: 1.82,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 0,
  reloadTime: 0,
  ultCost: 3400,
  abilities: {
    [Slot.Primary]: { name: "Sorrow Edge", key: "LMB", description: "Slash in a broad arc. After a portal dash, spin instead.", cooldown: 0 },
    [Slot.Secondary]: { name: "Rift Cut", key: "RMB", description: "Charge and release a piercing blade wave, or summon a rift claw after dashing.", cooldown: 6 },
    [Slot.Ability1]: { name: "Understep", key: "Shift", description: "Fall through a portal and reappear ahead, briefly phased. Two charges.", cooldown: 6, charges: 2 },
    [Slot.Ability2]: { name: "Upcast Lunge", key: "E", description: "Launch forward and uppercut enemies into the air.", cooldown: 8 },
    [Slot.Ultimate]: { name: "Tragic Omen", key: "Q", description: "Transform for 12 seconds, empowering every slash and dash.", cooldown: 0 },
  },
  passive: { name: "Pain Dividend", description: "Thirty percent of damage Tragik deals becomes temporary bonus health, up to 150." },
};

const primarySlash = (sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, input: Parameters<HeroModule["kit"]["tick"]>[2]) => {
  const c = sim.c;
  const dark = c.Ult.active[eid]! > 0;
  const combo = hv(c, eid, V.combo) > 0;
  action(sim, eid, combo ? ActionKind.AltFire : ActionKind.Fire, combo ? 20 : 12);
  c.Weapon.fireCd[eid] = combo ? (dark ? 55 : 35) : dark ? 60 : 50;
  if (combo) {
    const center = add(sim.posOf(eid), { x: 0, y: 1, z: 0 });
    sim.emit({ type: "explosion", pos: center, radius: dark ? 12 : 6, kind: FX.Rift });
    const hits = dark ? 3 : 1;
    for (let i = 0; i < hits; i++)
      for (const { eid: t } of playersInRadius(sim, center, dark ? 12 : 6, (e) => isEnemy(sim, eid, e), false)) applyDamage(sim, t, dark ? 45 : 50, eid);
    setHv(c, eid, V.combo, 0);
    return;
  }
  for (const t of coneTargets(sim, eid, dark ? 10 : 6.5, dark ? 1.25 : 0.95, (e) => isEnemy(sim, eid, e))) {
    const d = Math.hypot(c.Pos.x[t]! - c.Pos.x[eid]!, c.Pos.z[t]! - c.Pos.z[eid]!);
    const start = dark ? 6 : 4.5;
    const end = dark ? 10 : 6.5;
    const min = dark ? 0.25 : 0.5;
    const k = d <= start ? 1 : d >= end ? min : 1 - ((d - start) / (end - start)) * (1 - min);
    applyDamage(sim, t, (dark ? 105 : 75) * k, eid);
  }
  for (const { eid: t } of coneConstructs(sim, eid, dark ? 10 : 6.5, dark ? 1.25 : 0.95)) damageConstruct(sim, t, dark ? 105 : 75, eid);
  void input;
};

const fireSlash = (sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, input: Parameters<HeroModule["kit"]["tick"]>[2], charge: number) => {
  const dark = sim.c.Ult.active[eid]! > 0;
  const maxT = dark ? 1.8 : 1.2;
  const k = Math.min(1, charge / maxT);
  const dmg = dark ? 90 + 90 * k : 45 + 36 * k;
  sim.c.Weapon.fireCd[eid] = secs(dark ? 3 : 6);
  action(sim, eid, ActionKind.AltFire, 12);
  const a = aim(sim, eid);
  const dir = spreadDir(a.dir, 0, sim.c.Weapon.shots[eid]!);
  const origin = add(a.origin, scale(dir, 0.5));
  spawnProjectile(sim, eid, { kind: PK.RiftSlash, origin, vel: scale(dir, dark ? 100 : 80), seconds: 1.5, radius: dark ? 0.28 : 0.18, seq: input.seq });
  setHv(sim.c, eid, V.slashReady, dmg);
};

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.combo, 0);
      setHv(sim.c, eid, V.charge, 0);
      setHv(sim.c, eid, V.ultGlow, 0);
      setHv(sim.c, eid, V.slashReady, 0);
      setHv(sim.c, eid, V.touched, 0);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.combo) > 0) setHv(c, eid, V.combo, Math.max(0, hv(c, eid, V.combo) - DT));
      setHv(c, eid, V.ultGlow, c.Ult.active[eid]! > 0 ? c.Ult.active[eid]! / ULT_TICKS : 0);
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;
      if (useUlt(sim, eid, input, pressed)) {
        action(sim, eid, ActionKind.Ult, secs(0.8));
        c.Ult.active[eid] = ULT_TICKS;
        setHv(c, eid, V.ultGlow, 1);
        for (const slot of [Slot.Ability1, Slot.Ability2, Slot.Secondary]) {
          const i = eid * 6 + slot;
          c.Abil.cd[i] = 0;
          c.Abil.charges[i] = sim.hero(eid).abilities[slot]?.charges ?? 1;
        }
        applyStatus(sim, eid, "resist", 1.5, { source: eid });
      }
      const charging = !!(input.buttons & Btn.Secondary) && hv(c, eid, V.combo) <= 0 && c.Weapon.fireCd[eid]! <= 0;
      if (charging) {
        setHv(c, eid, V.charge, Math.min(2, hv(c, eid, V.charge) + DT));
        action(sim, eid, ActionKind.Cast, 2);
      } else if (hv(c, eid, V.charge) > 0) {
        fireSlash(sim, eid, input, hv(c, eid, V.charge));
        setHv(c, eid, V.charge, 0);
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        const dark = c.Ult.active[eid]! > 0;
        if (dark) c.Abil.cd[eid * 6 + Slot.Ability1] = secs(3);
        const d = wishDir(input);
        startMove(sim, eid, MoveKind.Dash, secs(10 / (dark ? 17 : 12)), dark ? 17 : 12, { x: d.x * (dark ? 17 : 12), y: -2, z: d.z * (dark ? 17 : 12) });
        applyStatus(sim, eid, "phased", 0.7, { source: eid });
        setHv(c, eid, V.combo, 3);
      }
      if (pressed & Btn.Ability2 && ready(sim, eid, Slot.Ability2)) {
        consume(sim, eid, Slot.Ability2);
        const dark = c.Ult.active[eid]! > 0;
        if (dark) c.Abil.cd[eid * 6 + Slot.Ability2] = secs(6);
        const d = wishDir(input);
        setHv(c, eid, V.touched, 0);
        startMove(sim, eid, HERO_DASH, secs((dark ? 20 : 16) / (dark ? 60 : 46)), dark ? 60 : 46, { x: d.x * (dark ? 60 : 46), y: 0, z: d.z * (dark ? 60 : 46) });
      }
      if (hv(c, eid, V.combo) > 0 && pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        const center = add(sim.posOf(eid), { x: 0, y: 1, z: 0 });
        explode(sim, eid, center, { radius: c.Ult.active[eid]! > 0 ? 4 : 3, damage: c.Ult.active[eid]! > 0 ? 100 : 80, minDamage: 20, fxKind: FX.Rift });
        setHv(c, eid, V.combo, 0);
      } else if (!charging && trigger(sim, eid, !!(input.buttons & Btn.Primary), true)) primarySlash(sim, eid, input);
    },
    scripted(sim, eid, _input, kind) {
      if (kind !== HERO_DASH) return;
      const dark = sim.c.Ult.active[eid]! > 0;
      contactEnemies(sim, eid, 1, (t) => {
        const bit = 1 << (sim.c.Net.id[t]! % 24);
        const seen = hv(sim.c, eid, V.touched);
        if ((seen & bit) !== 0) return;
        setHv(sim.c, eid, V.touched, seen | bit);
        applyDamage(sim, t, dark ? 140 : 85, eid);
        knockUp(sim, t, dark ? 9 : 7);
      });
    },
    onDealtDamage(sim, attacker, _target, dealt) {
      if (dealt > 0) addOverhealth(sim, attacker, Math.min(150, sim.c.Health.over[attacker]! + dealt * 0.3), secs(5), attacker);
    },
  },
  projectiles: {
    [PK.RiftSlash]: riftSlashProjectile,
  },
  fx: { [FX.Rift]: { color: "#ff7a3d" } },
};

export default module;
